import React, { useEffect, useMemo, useState } from 'react';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { formatMagentoDateTime } from '../utils/datetime';
import { buildOrderMetrics, RISK48_HOURS, RISK72_HOURS } from '../utils/orderMetrics';
import './ExecutiveOverviewPage.css';

const PERIOD_OPTIONS = {
  '24h': { label: '24h', hours: 24 },
  '7': { label: '7d', days: 7 },
  '30': { label: '30d', days: 30 },
  '180': { label: '6m', days: 180 },
  '365': { label: '1y', days: 365 },
};

const pad = (value) => String(value).padStart(2, '0');

const formatMagentoDateTimeRange = (date) => {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
};

const buildPresetRange = (periodKey) => {
  const now = new Date();
  const option = PERIOD_OPTIONS[periodKey] || PERIOD_OPTIONS['24h'];
  const end = new Date(now);
  const start = new Date(end);

  if (option.hours) {
    start.setHours(start.getHours() - option.hours);
  } else {
    start.setDate(start.getDate() - ((option.days || 1) - 1));
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  }

  return { start, end, label: option.label };
};

const buildCustomRange = (fromValue, toValue) => {
  if (!fromValue || !toValue) {
    throw new Error('Please choose both custom dates.');
  }
  const start = new Date(`${fromValue}T00:00:00`);
  const end = new Date(`${toValue}T23:59:59`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new Error('Invalid custom date range.');
  }
  if (start.getTime() > end.getTime()) {
    throw new Error('Custom start date must be before the end date.');
  }
  return { start, end, label: `${fromValue} to ${toValue}` };
};

const buildPreviousRange = ({ start, end }) => {
  const durationMs = end.getTime() - start.getTime();
  const previousEnd = new Date(start.getTime() - 1000);
  const previousStart = new Date(previousEnd.getTime() - durationMs);
  return { start: previousStart, end: previousEnd };
};

const uniqByEntityId = (orders = []) => {
  const map = new Map();
  orders.forEach((order) => {
    const key = order?.entity_id || order?.increment_id;
    if (key) map.set(String(key), order);
  });
  return Array.from(map.values());
};

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[\",\n]/.test(str)) {
    return `"${str.replace(/\"/g, '""')}"`;
  }
  return str;
};

const downloadCsv = (filename, headers, rows) => {
  const lines = [
    headers.map(csvEscape).join(','),
    ...rows.map((row) => row.map(csvEscape).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ExecutiveOverviewPage = () => {
  const [periodKey, setPeriodKey] = useState('24h');
  const [customFrom, setCustomFrom] = useState(() => formatDateInputValue(new Date()));
  const [customTo, setCustomTo] = useState(() => formatDateInputValue(new Date()));
  const [appliedCustomRange, setAppliedCustomRange] = useState(null);
  const [periodOrders, setPeriodOrders] = useState([]);
  const [previousPeriodOrders, setPreviousPeriodOrders] = useState([]);
  const [backlogOrders, setBacklogOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const restBase = (localStorage.getItem('restBase') || '/rest/V1').trim() || '/rest/V1';
  const pickingStatus = (localStorage.getItem('pickingStatus') || 'picking').trim();

  const loadOverview = async () => {
    setLoading(true);
    setError('');
    try {
      const selectedRange = periodKey === 'custom' && appliedCustomRange
        ? buildCustomRange(appliedCustomRange.from, appliedCustomRange.to)
        : buildPresetRange(periodKey);
      const previousRange = buildPreviousRange(selectedRange);
      const fromDate = formatMagentoDateTimeRange(selectedRange.start);
      const toDate = formatMagentoDateTimeRange(selectedRange.end);
      const previousFromDate = formatMagentoDateTimeRange(previousRange.start);
      const previousToDate = formatMagentoDateTimeRange(previousRange.end);
      const pageSize = 100;

      const fetchAllOrderPages = async (baseUrl, label) => {
        const allItems = [];
        let currentPage = 1;
        let totalCount = null;

        while (totalCount === null || allItems.length < totalCount) {
          const pagedUrl =
            `${baseUrl}&searchCriteria[pageSize]=${pageSize}` +
            `&searchCriteria[currentPage]=${currentPage}`;
          const response = await apiFetch(pagedUrl, { headers: getAuthHeaders() });
          if (!response.ok) {
            throw new Error((await response.text()) || `Failed to load ${label} (${response.status})`);
          }

          const data = await response.json();
          const items = Array.isArray(data?.items) ? data.items : [];
          totalCount = Number(data?.total_count);
          if (!Number.isFinite(totalCount)) totalCount = allItems.length + items.length;
          allItems.push(...items);

          if (!items.length || items.length < pageSize) break;
          currentPage += 1;
        }

        return allItems;
      };

      const rangeUrl =
        `${restBase}/orders?` +
        'searchCriteria[filter_groups][0][filters][0][field]=created_at' +
        `&searchCriteria[filter_groups][0][filters][0][value]=${encodeURIComponent(fromDate)}` +
        '&searchCriteria[filter_groups][0][filters][0][condition_type]=gteq' +
        '&searchCriteria[filter_groups][1][filters][0][field]=created_at' +
        `&searchCriteria[filter_groups][1][filters][0][value]=${encodeURIComponent(toDate)}` +
        '&searchCriteria[filter_groups][1][filters][0][condition_type]=lteq';
      const previousRangeUrl =
        `${restBase}/orders?` +
        'searchCriteria[filter_groups][0][filters][0][field]=created_at' +
        `&searchCriteria[filter_groups][0][filters][0][value]=${encodeURIComponent(previousFromDate)}` +
        '&searchCriteria[filter_groups][0][filters][0][condition_type]=gteq' +
        '&searchCriteria[filter_groups][1][filters][0][field]=created_at' +
        `&searchCriteria[filter_groups][1][filters][0][value]=${encodeURIComponent(previousToDate)}` +
        '&searchCriteria[filter_groups][1][filters][0][condition_type]=lteq';

      const processingBacklogUrl =
        `${restBase}/orders?` +
        'searchCriteria[filter_groups][0][filters][0][field]=status' +
        '&searchCriteria[filter_groups][0][filters][0][value]=processing' +
        '&searchCriteria[filter_groups][0][filters][0][condition_type]=eq';
      const pickingBacklogUrl =
        `${restBase}/orders?` +
        'searchCriteria[filter_groups][0][filters][0][field]=status' +
        `&searchCriteria[filter_groups][0][filters][0][value]=${encodeURIComponent(pickingStatus)}` +
        '&searchCriteria[filter_groups][0][filters][0][condition_type]=eq';

      const [rangeItems, previousRangeItems, processingBacklog, pickingBacklog] = await Promise.all([
        fetchAllOrderPages(rangeUrl, 'orders'),
        fetchAllOrderPages(previousRangeUrl, 'previous period orders'),
        fetchAllOrderPages(processingBacklogUrl, 'processing backlog'),
        fetchAllOrderPages(pickingBacklogUrl, 'picking backlog'),
      ]);
      const backlogItems = uniqByEntityId([...processingBacklog, ...pickingBacklog]);

      const loadDetails = async (order) => {
        try {
          const detailRes = await apiFetch(`${restBase}/orders/${order.entity_id}`, {
            headers: getAuthHeaders(),
          });
          if (!detailRes.ok) throw new Error(await detailRes.text());
          return await detailRes.json();
        } catch (detailErr) {
          console.warn('Failed to fetch order detail', order.entity_id, detailErr);
          return order;
        }
      };

      const [rangeDetailed, previousRangeDetailed, backlogDetailed] = await Promise.all([
        Promise.all(rangeItems.map(loadDetails)),
        Promise.all(previousRangeItems.map(loadDetails)),
        Promise.all(backlogItems.map(loadDetails)),
      ]);

      setPeriodOrders(rangeDetailed.filter(Boolean));
      setPreviousPeriodOrders(previousRangeDetailed.filter(Boolean));
      setBacklogOrders(backlogDetailed.filter(Boolean));
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, [periodKey, appliedCustomRange]);

  const buildPeriodSummary = (orders = [], now = new Date()) => {
    const base = {
      orders: orders.length,
      revenue: 0,
      sla48Compliant: 0,
      sla72Compliant: 0,
      sla48Eligible: 0,
      sla72Eligible: 0,
      shippingMix: {},
      heroSkus: new Map(),
      villainSkus: new Map(),
      skuIssues: new Map(),
    };

    orders.forEach((order) => {
      base.revenue += Number(order.grand_total) || 0;
      const orderMetrics = buildOrderMetrics(order, { now, pickingStatus });
      const shippingLabel = orderMetrics.shippingLabel;
      base.shippingMix[shippingLabel] = (base.shippingMix[shippingLabel] || 0) + 1;

      const sla = orderMetrics.sla;
      if (sla.hoursToPicking !== null) {
        base.sla48Eligible += 1;
        if (sla.sla48Compliant) base.sla48Compliant += 1;
      }
      if (sla.hoursToComplete !== null) {
        base.sla72Eligible += 1;
        if (sla.sla72Compliant) base.sla72Compliant += 1;
      }

      const isDelayed = sla.sla48Breached || sla.sla72Breached;
      const items = Array.isArray(order.items) ? order.items : [];
      items.forEach((item) => {
        const sku = item.sku || 'Unknown';
        const qty = Number(item.qty_ordered) || 0;
        if (!sku || qty <= 0) return;
        base.heroSkus.set(sku, (base.heroSkus.get(sku) || 0) + qty);

        const issue = base.skuIssues.get(sku) || {
          sku,
          totalQty: 0,
          delayedQty: 0,
          delayedOrders: new Set(),
        };
        issue.totalQty += qty;
        if (isDelayed) {
          base.villainSkus.set(sku, (base.villainSkus.get(sku) || 0) + qty);
          issue.delayedQty += qty;
          issue.delayedOrders.add(order.increment_id || order.entity_id);
        }
        base.skuIssues.set(sku, issue);
      });
    });

    const formatTop = (map) => Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([sku, qty]) => ({ sku, qty }));

    const skuIssueTop = Array.from(base.skuIssues.values())
      .filter((entry) => entry.delayedQty > 0)
      .sort((a, b) => b.delayedQty - a.delayedQty || b.delayedOrders.size - a.delayedOrders.size)
      .slice(0, 5)
      .map((entry) => ({
        sku: entry.sku,
        delayedQty: entry.delayedQty,
        totalQty: entry.totalQty,
        delayedOrders: entry.delayedOrders.size,
      }));

    const sla48Rate = base.sla48Eligible ? (base.sla48Compliant / base.sla48Eligible) * 100 : 0;
    const sla72Rate = base.sla72Eligible ? (base.sla72Compliant / base.sla72Eligible) * 100 : 0;

    return {
      ...base,
      sla48Rate,
      sla72Rate,
      heroTop: formatTop(base.heroSkus),
      villainTop: formatTop(base.villainSkus),
      skuIssueTop,
    };
  };

  const comparison = useMemo(() => {
    const now = new Date();
    const current = buildPeriodSummary(periodOrders, now);
    const previous = buildPeriodSummary(previousPeriodOrders, now);
    const changePercent = (currentValue, previousValue) => {
      if (!previousValue) return currentValue ? 100 : 0;
      return ((currentValue - previousValue) / previousValue) * 100;
    };
    const changePoints = (currentValue, previousValue) => currentValue - previousValue;

    return {
      current,
      previous,
      deltas: {
        orders: changePercent(current.orders, previous.orders),
        revenue: changePercent(current.revenue, previous.revenue),
        sla48Rate: changePoints(current.sla48Rate, previous.sla48Rate),
        sla72Rate: changePoints(current.sla72Rate, previous.sla72Rate),
      },
    };
  }, [periodOrders, previousPeriodOrders, pickingStatus]);

  const metrics = comparison.current;

  const backlogStats = useMemo(() => {
    const now = new Date();
    const stats = {
      beforePicking: 0,
      inPicking: 0,
      atRisk48: 0,
      atRisk72: 0,
      overdue48: 0,
      overdue72: 0,
      frozenFreshAtRisk: 0,
      nextDayAtRisk: 0,
      revenueAtRisk: 0,
      revenueOverdue: 0,
      agingBuckets: {
        '0-12h': 0,
        '12-24h': 0,
        '24-48h': 0,
        '48-72h': 0,
        '72h+': 0,
      },
      shippingPerformance: {},
      exceptionOrders: [],
    };

    backlogOrders.forEach((order) => {
      const orderMetrics = buildOrderMetrics(order, { now, pickingStatus });
      const sla = orderMetrics.sla;
      const value = Number(order.grand_total) || 0;
      const isAtRisk = sla.risk48 || sla.risk72;
      const isOverdue = sla.sla48Breached || sla.sla72Breached;
      const shipping = orderMetrics.shippingLabel;
      const age = orderMetrics.ageHours || 0;

      if (!sla.pickingAt && !sla.completeAt) stats.beforePicking += 1;
      if (sla.pickingAt && !sla.completeAt) stats.inPicking += 1;
      if (sla.risk48) stats.atRisk48 += 1;
      if (sla.risk72) stats.atRisk72 += 1;
      if (sla.sla48Breached) stats.overdue48 += 1;
      if (sla.sla72Breached) stats.overdue72 += 1;
      if (isAtRisk || isOverdue) stats.revenueAtRisk += value;
      if (isOverdue) stats.revenueOverdue += value;
      if ((orderMetrics.hasFrozen || orderMetrics.hasFresh) && (isAtRisk || isOverdue)) {
        stats.frozenFreshAtRisk += 1;
      }
      if (shipping === 'Next-day' && (isAtRisk || isOverdue)) {
        stats.nextDayAtRisk += 1;
      }

      if (age < 12) stats.agingBuckets['0-12h'] += 1;
      else if (age < 24) stats.agingBuckets['12-24h'] += 1;
      else if (age < 48) stats.agingBuckets['24-48h'] += 1;
      else if (age < 72) stats.agingBuckets['48-72h'] += 1;
      else stats.agingBuckets['72h+'] += 1;

      const shippingEntry = stats.shippingPerformance[shipping] || {
        shipping,
        total: 0,
        atRisk: 0,
        overdue48: 0,
        overdue72: 0,
      };
      shippingEntry.total += 1;
      if (isAtRisk) shippingEntry.atRisk += 1;
      if (sla.sla48Breached) shippingEntry.overdue48 += 1;
      if (sla.sla72Breached) shippingEntry.overdue72 += 1;
      stats.shippingPerformance[shipping] = shippingEntry;

      if (isAtRisk || isOverdue || (sla.pickingAt && !sla.completeAt)) {
        const flags = [];
        if (sla.sla72Breached) flags.push('SLA72 overdue');
        else if (sla.sla48Breached) flags.push('SLA48 overdue');
        else if (sla.risk72) flags.push(`SLA72 risk ${RISK72_HOURS}h+`);
        else if (sla.risk48) flags.push(`SLA48 risk ${RISK48_HOURS}h+`);
        if (orderMetrics.hasFrozen) flags.push('Frozen');
        if (orderMetrics.hasFresh) flags.push('Fresh');
        if (orderMetrics.hasWarehouse) flags.push('Warehouse');
        if (sla.pickingAt && !sla.completeAt) flags.push('In picking');

        stats.exceptionOrders.push({
          id: order.increment_id || order.entity_id,
          status: order.status || 'N/A',
          shipping,
          age,
          value,
          flags,
        });
      }
    });

    stats.shippingPerformance = Object.values(stats.shippingPerformance)
      .sort((a, b) => (b.overdue72 + b.overdue48 + b.atRisk) - (a.overdue72 + a.overdue48 + a.atRisk));
    stats.exceptionOrders = stats.exceptionOrders
      .sort((a, b) => b.age - a.age || b.value - a.value)
      .slice(0, 10);

    return stats;
  }, [backlogOrders, pickingStatus]);

  const formatDeltaPercent = (value) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const formatDeltaPoints = (value) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)} pts`;
  };

  const deltaClassName = (value, inverse = false) => {
    if (value === 0) return 'neutral';
    const isGood = inverse ? value < 0 : value > 0;
    return isGood ? 'positive' : 'negative';
  };

  const totalBacklog = backlogStats.beforePicking + backlogStats.inPicking;
  const maxAgingBucket = Math.max(...Object.values(backlogStats.agingBuckets), 1);
  const maxShippingMix = Math.max(...Object.values(metrics.shippingMix), 1);
  const maxSkuIssueQty = Math.max(...metrics.skuIssueTop.map((entry) => entry.delayedQty), 1);
  const maxOverdueSkuQty = Math.max(...metrics.villainTop.map((entry) => entry.qty), 1);
  const maxHeroSkuQty = Math.max(...metrics.heroTop.map((entry) => entry.qty), 1);
  const riskScore = backlogStats.atRisk48 + backlogStats.atRisk72 + backlogStats.overdue48 + backlogStats.overdue72;
  const riskLevel = riskScore === 0 ? 'Controlled' : riskScore <= 3 ? 'Watch' : 'Critical';
  const riskLevelClass = riskLevel.toLowerCase();

  const handleExportCsv = () => {
    const now = new Date();
    const selectedRange = periodKey === 'custom' && appliedCustomRange
      ? buildCustomRange(appliedCustomRange.from, appliedCustomRange.to)
      : buildPresetRange(periodKey);
    const periodLabel = selectedRange.label;
    const addRows = (section, entries) => entries.map((entry) => [section, ...entry]);
    const headers = ['section', 'metric', 'value', 'detail'];
    const rows = [
      ...addRows('Report', [
        ['Period', periodLabel, `${formatMagentoDateTimeRange(selectedRange.start)} to ${formatMagentoDateTimeRange(selectedRange.end)}`],
        ['Generated at', formatMagentoDateTimeRange(now), 'Local browser time'],
        ['Risk level', riskLevel, `${riskScore} risk signals`],
      ]),
      ...addRows('Performance KPIs', [
        ['Orders', metrics.orders, `${formatDeltaPercent(comparison.deltas.orders)} vs previous period`],
        ['Revenue', metrics.revenue.toFixed(2), `${formatDeltaPercent(comparison.deltas.revenue)} vs previous period`],
        ['SLA48 compliance', `${metrics.sla48Rate.toFixed(1)}%`, `${metrics.sla48Compliant}/${metrics.sla48Eligible}; ${formatDeltaPoints(comparison.deltas.sla48Rate)} vs previous period`],
        ['SLA72 compliance', `${metrics.sla72Rate.toFixed(1)}%`, `${metrics.sla72Compliant}/${metrics.sla72Eligible}; ${formatDeltaPoints(comparison.deltas.sla72Rate)} vs previous period`],
        ['Backlog before picking', backlogStats.beforePicking, 'Orders waiting for picker assignment'],
        ['Backlog in picking', backlogStats.inPicking, 'Orders currently being picked'],
      ]),
      ...addRows('Risk', [
        ['SLA48 at risk', backlogStats.atRisk48, `Open orders at ${RISK48_HOURS}h+ before picking`],
        ['SLA72 at risk', backlogStats.atRisk72, `Open orders at ${RISK72_HOURS}h+ before completion`],
        ['SLA48 overdue', backlogStats.overdue48, 'Open orders beyond SLA48'],
        ['SLA72 overdue', backlogStats.overdue72, 'Open orders beyond SLA72'],
        ['Revenue at risk', backlogStats.revenueAtRisk.toFixed(2), 'Open value tied to risk or overdue orders'],
        ['Overdue revenue', backlogStats.revenueOverdue.toFixed(2), 'Open value already beyond SLA48/SLA72'],
        ['Fresh/Frozen risk', backlogStats.frozenFreshAtRisk, 'Temperature-sensitive open orders at risk'],
        ['Next-day risk', backlogStats.nextDayAtRisk, 'Next-day open orders at risk'],
      ]),
      ...Object.entries(backlogStats.agingBuckets).map(([label, count]) => [
        'Backlog Aging',
        label,
        count,
        `${totalBacklog ? ((count / totalBacklog) * 100).toFixed(1) : '0.0'}% of open backlog`,
      ]),
      ...backlogStats.shippingPerformance.map((entry) => [
        'Shipping Performance',
        entry.shipping,
        entry.total,
        `${entry.atRisk} risk; ${entry.overdue48} SLA48 overdue; ${entry.overdue72} SLA72 overdue`,
      ]),
      ...Object.entries(metrics.shippingMix).map(([label, count]) => [
        'Shipping Mix',
        label,
        count,
        `${metrics.orders ? ((count / metrics.orders) * 100).toFixed(1) : '0.0'}% of period orders`,
      ]),
      ...metrics.skuIssueTop.map((entry) => [
        'SKU Issue Detection',
        entry.sku,
        entry.delayedQty,
        `${entry.delayedOrders} delayed orders; ${entry.totalQty} total qty`,
      ]),
      ...metrics.villainTop.map((entry) => [
        'Top Overdue SKUs',
        entry.sku,
        entry.qty,
        'Quantity in overdue orders',
      ]),
      ...metrics.heroTop.map((entry) => [
        'Top SKUs by Quantity',
        entry.sku,
        entry.qty,
        'Ordered quantity in selected period',
      ]),
      ...backlogStats.exceptionOrders.map((entry) => [
        'Exception Queue',
        `#${entry.id}`,
        entry.value.toFixed(2),
        `${entry.status}; ${entry.shipping}; ${entry.age.toFixed(1)}h; ${entry.flags.join(' | ')}`,
      ]),
    ];
    const stamp = now.toISOString().slice(0, 10);
    downloadCsv(`executive-overview-formatted-${stamp}.csv`, headers, rows);
  };

  const handleExportPdf = () => {
    window.print();
  };

  const handlePresetSelect = (key) => {
    setPeriodKey(key);
  };

  const handleApplyCustomRange = () => {
    try {
      buildCustomRange(customFrom, customTo);
      setAppliedCustomRange({ from: customFrom, to: customTo });
      setPeriodKey('custom');
      setError('');
    } catch (err) {
      setError(err.message || 'Invalid custom date range.');
    }
  };

  return (
    <div className="overview-page">
      <div className="overview-shell">
        <header className="overview-header">
          <div>
            <h1>Executive Overview</h1>
            <p>Operational KPIs and SLA for the selected period.</p>
            {lastUpdated && (
              <small>Last updated: {formatMagentoDateTime(lastUpdated)}</small>
            )}
          </div>
          <div className="overview-actions no-print">
            <div className="overview-period-tools">
              <div className="overview-toggle">
              {Object.entries(PERIOD_OPTIONS).map(([key, option]) => (
                <button
                  key={key}
                  type="button"
                  className={key === periodKey ? 'active' : ''}
                  onClick={() => handlePresetSelect(key)}
                >
                  {option.label}
                </button>
              ))}
              </div>
              <div className={`overview-custom-range ${periodKey === 'custom' ? 'active' : ''}`}>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(event) => setCustomFrom(event.target.value)}
                  aria-label="Custom start date"
                />
                <span>to</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(event) => setCustomTo(event.target.value)}
                  aria-label="Custom end date"
                />
                <button type="button" onClick={handleApplyCustomRange}>
                  Apply
                </button>
              </div>
            </div>
            <button className="overview-button" onClick={handleExportCsv}>
              Export CSV
            </button>
            <button className="overview-button" onClick={handleExportPdf}>
              Export PDF
            </button>
            <button className="overview-button" onClick={loadOverview}>
              Refresh
            </button>
          </div>
        </header>

        <LoadingOverlay show={loading} ariaLabel="Loading executive overview" />
        {error && !loading && <div className="overview-state overview-state--error">{error}</div>}

        {!loading && !error && (
          <section className="overview-report-hero">
            <div>
              <span className="overview-eyebrow">Executive report</span>
              <h2>Operational health for the selected period</h2>
              <p>
                {metrics.orders} orders, € {metrics.revenue.toFixed(2)} revenue, {totalBacklog} open backlog.
              </p>
            </div>
            <div className={`overview-risk-badge ${riskLevelClass}`}>
              <span>Risk level</span>
              <strong>{riskLevel}</strong>
              <small>{riskScore} risk signals</small>
            </div>
          </section>
        )}

        {!loading && !error && (
          <section className="overview-section">
            <div className="overview-section-heading">
              <div>
                <span className="overview-eyebrow">KPIs</span>
                <h2>Period performance</h2>
              </div>
              <small>Compared with the previous equivalent period</small>
            </div>
            <div className="overview-kpi-grid">
              <div className="overview-kpi-card">
                <span>Orders</span>
                <strong>{metrics.orders}</strong>
                <small className={deltaClassName(comparison.deltas.orders)}>
                  {formatDeltaPercent(comparison.deltas.orders)} vs previous
                </small>
              </div>
              <div className="overview-kpi-card">
                <span>Revenue</span>
                <strong>€ {metrics.revenue.toFixed(2)}</strong>
                <small className={deltaClassName(comparison.deltas.revenue)}>
                  {formatDeltaPercent(comparison.deltas.revenue)} vs previous
                </small>
              </div>
              <div className="overview-kpi-card">
                <span>SLA48 compliance</span>
                <strong>{metrics.sla48Rate.toFixed(1)}%</strong>
                <div className="overview-progress">
                  <i style={{ width: `${Math.min(metrics.sla48Rate, 100)}%` }} />
                </div>
                <small>{metrics.sla48Compliant}/{metrics.sla48Eligible}</small>
                <small className={deltaClassName(comparison.deltas.sla48Rate)}>
                  {formatDeltaPoints(comparison.deltas.sla48Rate)} vs previous
                </small>
              </div>
              <div className="overview-kpi-card">
                <span>SLA72 compliance</span>
                <strong>{metrics.sla72Rate.toFixed(1)}%</strong>
                <div className="overview-progress">
                  <i style={{ width: `${Math.min(metrics.sla72Rate, 100)}%` }} />
                </div>
                <small>{metrics.sla72Compliant}/{metrics.sla72Eligible}</small>
                <small className={deltaClassName(comparison.deltas.sla72Rate)}>
                  {formatDeltaPoints(comparison.deltas.sla72Rate)} vs previous
                </small>
              </div>
              <div className="overview-kpi-card">
                <span>Before picking</span>
                <strong>{backlogStats.beforePicking}</strong>
                <small>Orders waiting for picker assignment</small>
              </div>
              <div className="overview-kpi-card">
                <span>In picking</span>
                <strong>{backlogStats.inPicking}</strong>
                <small>Orders currently being picked</small>
              </div>
            </div>
          </section>
        )}

        {!loading && !error && (
          <section className="overview-section">
            <div className="overview-section-heading">
              <div>
                <span className="overview-eyebrow">Risk</span>
                <h2>Action center</h2>
              </div>
              <small>Backlog pressure, SLA risk, and revenue exposure</small>
            </div>
            <div className="overview-risk-grid">
              <div className="overview-risk-card warning">
                <span>SLA48 at risk</span>
                <strong>{backlogStats.atRisk48}</strong>
                <small>{RISK48_HOURS}h+ before picking</small>
              </div>
              <div className="overview-risk-card danger">
                <span>SLA72 at risk</span>
                <strong>{backlogStats.atRisk72}</strong>
                <small>{RISK72_HOURS}h+ before completion</small>
              </div>
              <div className="overview-risk-card danger wide">
                <span>Revenue at risk</span>
                <strong>€ {backlogStats.revenueAtRisk.toFixed(2)}</strong>
                <small>Open value tied to risk or overdue orders</small>
              </div>
              <div className="overview-risk-card danger wide">
                <span>Overdue revenue</span>
                <strong>€ {backlogStats.revenueOverdue.toFixed(2)}</strong>
                <small>Open value already beyond SLA48/SLA72</small>
              </div>
              <div className="overview-risk-card warning">
                <span>Fresh/Frozen risk</span>
                <strong>{backlogStats.frozenFreshAtRisk}</strong>
                <small>Temperature-sensitive orders</small>
              </div>
              <div className="overview-risk-card warning">
                <span>Next-day risk</span>
                <strong>{backlogStats.nextDayAtRisk}</strong>
                <small>Next-day open orders at risk</small>
              </div>
            </div>
          </section>
        )}

        {!loading && !error && (
          <section className="overview-section">
            <div className="overview-section-heading">
              <div>
                <span className="overview-eyebrow">Operations</span>
                <h2>Backlog and shipping profile</h2>
              </div>
              <small>Visual distribution of open work and shipping demand</small>
            </div>
            <div className="overview-panels">
              <div className="overview-panel overview-chart-card">
              <h2>Backlog Aging</h2>
              <ul className="overview-bar-list">
                {Object.entries(backlogStats.agingBuckets).map(([label, count]) => (
                  <li key={label}>
                    <div className="overview-bar-row">
                      <span>{label}</span>
                      <strong>{count}</strong>
                    </div>
                    <div className="overview-mini-bar">
                      <i style={{ width: `${Math.max((count / maxAgingBucket) * 100, count ? 4 : 0)}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="overview-panel overview-chart-card">
              <h2>Shipping Performance</h2>
              <ul className="overview-metric-list">
                {backlogStats.shippingPerformance.map((entry) => (
                  <li key={entry.shipping}>
                    <div>
                      <span>{entry.shipping}</span>
                      <small>{entry.atRisk} risk · {entry.overdue48} SLA48 · {entry.overdue72} SLA72</small>
                    </div>
                    <strong>{entry.total}</strong>
                  </li>
                ))}
                {!backlogStats.shippingPerformance.length && <li>No backlog data</li>}
              </ul>
            </div>
            <div className="overview-panel overview-chart-card">
              <h2>Shipping Mix</h2>
              <ul className="overview-bar-list">
                {Object.entries(metrics.shippingMix).map(([label, count]) => (
                  <li key={label}>
                    <div className="overview-bar-row">
                      <span>{label}</span>
                      <strong>{count}</strong>
                    </div>
                    <div className="overview-mini-bar blue">
                      <i style={{ width: `${Math.max((count / maxShippingMix) * 100, count ? 4 : 0)}%` }} />
                    </div>
                  </li>
                ))}
                {!Object.keys(metrics.shippingMix).length && <li>No data</li>}
              </ul>
              </div>
            </div>
          </section>
        )}

        {!loading && !error && (
          <section className="overview-section">
            <div className="overview-section-heading">
              <div>
                <span className="overview-eyebrow">Products</span>
                <h2>SKU intelligence</h2>
              </div>
              <small>Demand volume and SKUs connected to delayed work</small>
            </div>
            <div className="overview-panels">
              <div className="overview-panel overview-chart-card">
              <h2>SKU Issue Detection</h2>
              <ul className="overview-bar-list">
                {metrics.skuIssueTop.map((entry) => (
                  <li key={entry.sku}>
                    <div className="overview-bar-row">
                      <span>{entry.sku}</span>
                      <strong>{entry.delayedQty}</strong>
                    </div>
                    <div className="overview-mini-bar danger">
                      <i style={{ width: `${Math.max((entry.delayedQty / maxSkuIssueQty) * 100, entry.delayedQty ? 4 : 0)}%` }} />
                    </div>
                    <small>{entry.delayedOrders} delayed orders · {entry.totalQty} total qty</small>
                  </li>
                ))}
                {!metrics.skuIssueTop.length && <li>No delayed SKU pattern found</li>}
              </ul>
            </div>
            <div className="overview-panel overview-chart-card">
              <h2>Top 5 Overdue SKUs</h2>
              <ul className="overview-bar-list">
                {metrics.villainTop.map((entry) => (
                  <li key={entry.sku}>
                    <div className="overview-bar-row">
                      <span>{entry.sku}</span>
                      <strong>{entry.qty}</strong>
                    </div>
                    <div className="overview-mini-bar danger">
                      <i style={{ width: `${Math.max((entry.qty / maxOverdueSkuQty) * 100, entry.qty ? 4 : 0)}%` }} />
                    </div>
                  </li>
                ))}
                {!metrics.villainTop.length && <li>No data</li>}
              </ul>
            </div>
            <div className="overview-panel overview-chart-card">
              <h2>Top 5 SKUs by Quantity</h2>
              <ul className="overview-bar-list">
                {metrics.heroTop.map((entry) => (
                  <li key={entry.sku}>
                    <div className="overview-bar-row">
                      <span>{entry.sku}</span>
                      <strong>{entry.qty}</strong>
                    </div>
                    <div className="overview-mini-bar blue">
                      <i style={{ width: `${Math.max((entry.qty / maxHeroSkuQty) * 100, entry.qty ? 4 : 0)}%` }} />
                    </div>
                  </li>
                ))}
                {!metrics.heroTop.length && <li>No data</li>}
              </ul>
              </div>
            </div>
          </section>
        )}

        {!loading && !error && (
          <section className="overview-panel overview-exceptions overview-section">
            <div className="overview-section-heading">
              <div>
                <span className="overview-eyebrow">Exceptions</span>
                <h2>Queue requiring attention</h2>
              </div>
              <small>Prioritized by age and order value</small>
            </div>
            {backlogStats.exceptionOrders.length ? (
              <div className="overview-table-wrap">
                <table className="overview-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Shipping</th>
                      <th>Age</th>
                      <th>Value</th>
                      <th>Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backlogStats.exceptionOrders.map((entry) => (
                      <tr key={entry.id}>
                        <td>#{entry.id}</td>
                        <td>{entry.status}</td>
                        <td>{entry.shipping}</td>
                        <td>{entry.age.toFixed(1)}h</td>
                        <td>€ {entry.value.toFixed(2)}</td>
                        <td>
                          <div className="overview-flags">
                            {entry.flags.map((flag) => (
                              <span key={flag}>{flag}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overview-state">No exceptions in the current backlog.</div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default ExecutiveOverviewPage;
